import * as React from 'react';

import * as colors from 'libs/colors';
import { CheckIcon, ChevronLeftIcon } from 'components/icons';
import Paragraph from 'components/atoms/Paragraph';
import Text from 'components/atoms/Text';
import Divider from 'components/atoms/Divider';
import Spacer from 'components/atoms/Spacer';
import Box, { BoxItem } from 'components/atoms/Box';
import TopBar, { LeftItems } from 'components/molecules/TopBar';
import SearchInput from 'components/molecules/SearchInput';
import Footer from '../Footer';

import type { Brand } from 'components/combi-search/types';
import { gtmClassName } from 'components/combi-search/libs/gtm';

import cs from './index.scss';

type Props = {
  brands: Brand[];
  selectedBrandIds: number[];
  onChange?: (brandIds: number[]) => void;
  onSubmit: (brandIds: number[]) => void;
  onClose: () => void;
};

const alphabetList = [...Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ'), 'その他'];

const BrandSelector: React.FC<Props> = ({ brands, selectedBrandIds, onChange, onSubmit, onClose }) => {
  const [keyword, setKeyword] = React.useState('');
  const [workingIds, setWorkingIds] = React.useState<number[]>(selectedBrandIds);

  React.useEffect(() => {
    setWorkingIds(selectedBrandIds);
  }, [selectedBrandIds]);

  const trimmedKeyword = keyword.trim().toLowerCase();

  const filtered = React.useMemo(() => {
    if (trimmedKeyword === '') return brands;
    return brands.filter((brand) => brand.name.toLowerCase().includes(trimmedKeyword));
  }, [brands, trimmedKeyword]);

  const groups = React.useMemo(() => {
    const grouped: Record<string, Brand[]> = {};
    filtered.forEach((brand) => {
      const initial = normalizeInitial(brand.initial);
      if (!grouped[initial]) {
        grouped[initial] = [];
      }
      grouped[initial].push(brand);
    });
    // ソートはA-Z順、Zにその他を含める
    return alphabetList
      .filter((letter) => grouped[letter])
      .map((letter) => ({
        initial: letter,
        items: grouped[letter],
      }));
  }, [filtered]);

  const sectionRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const toggleBrand = React.useCallback(
    (brandId: number) => {
      setWorkingIds((prev) => {
        const exists = prev.includes(brandId);
        if (exists) {
          return prev.filter((id) => id !== brandId);
        }
        return [...prev, brandId];
      });
    },
    [setWorkingIds]
  );

  const handleSubmit = React.useCallback(() => {
    onChange?.(workingIds);
    onSubmit(workingIds);
  }, [onChange, onSubmit, workingIds]);

  const handleClear = React.useCallback(() => {
    setWorkingIds([]);
    onChange?.([]);
    onSubmit([]);
  }, [onChange, onSubmit]);

  const scrollToInitial = React.useCallback((letter: string) => {
    const ref = sectionRefs.current[letter];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const selectionPreview = React.useMemo(() => {
    if (workingIds.length === 0) return '選択なし';
    return `${workingIds.length}件選択中`;
  }, [workingIds]);

  if (brands.length === 0) return null;

  return (
    <div className={cs['container']}>
      <TopBar>
        <LeftItems>
          <Box
            horizontal
            padding="18px"
            align="center"
            onClick={onClose}
          >
            <ChevronLeftIcon color={colors.gray50} />
            <Spacer size={5} />
            <Text
              size="15px"
              color={colors.gray50}
            >
              戻る
            </Text>
          </Box>
        </LeftItems>
        <Paragraph
          align="center"
          size="18px"
          bold
          color={colors.black}
        >
          ブランド
        </Paragraph>
      </TopBar>
      <Divider />

      <Box
        padding="14px 16px"
        bg={colors.white}
      >
        <SearchInput
          placeholder="ブランド名で検索"
          value={keyword}
          onChange={setKeyword}
        />
        <Spacer size={10} />
        <Paragraph
          size="12px"
          color={colors.gray50}
        >
          {selectionPreview}
        </Paragraph>
      </Box>
      <Divider />

      <div className={cs['brand-list-container']}>
        <div
          className={cs['brand-list']}
          role="list"
        >
          {groups.map((group) => (
            <div
              key={group.initial}
              ref={(node) => {
                sectionRefs.current[group.initial] = node;
              }}
            >
              <Box
                padding="10px 16px"
                bg={colors.gray10}
              >
                <Paragraph
                  size="12px"
                  color={colors.gray50}
                  bold
                >
                  {group.initial}
                </Paragraph>
              </Box>
              {group.items.map((brand) => {
                const checked = workingIds.includes(brand.id);
                return (
                  <React.Fragment key={brand.id}>
                    <Divider />
                    <label
                      className={`${cs['brand-row']} ${gtmClassName.detailBrand}`}
                      onClick={() => toggleBrand(brand.id)}
                    >
                      <Box
                        horizontal
                        align="center"
                        padding="14px 16px"
                        bg={colors.white}
                        space="between"
                      >
                        <BoxItem
                          scale={1}
                          className={cs['brand-name-wrapper']}
                        >
                          <Paragraph
                            align="left"
                            size="14px"
                            color={colors.gray90}
                            className={cs['brand-name']}
                            maxLines={1}
                          >
                            {brand.name}
                          </Paragraph>
                        </BoxItem>
                        {checked ? (
                          <CheckIcon
                            color={colors.softOrange}
                            size="16px"
                          />
                        ) : null}
                      </Box>
                    </label>
                  </React.Fragment>
                );
              })}
            </div>
          ))}
          {groups.length === 0 ? (
            <Box
              padding="16px"
              bg={colors.white}
            >
              <Paragraph
                size="13px"
                color={colors.gray50}
                align="center"
              >
                該当するブランドがありません
              </Paragraph>
            </Box>
          ) : null}
        </div>

        <div
          className={cs['alphabet-rail']}
          aria-label="アルファベットジャンプ"
        >
          {alphabetList.map((letter) => (
            <button
              key={letter}
              type="button"
              className={cs['alphabet-button']}
              onClick={() => scrollToInitial(letter)}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      <Footer
        submitButtonText="決定"
        clearButtonText="指定しない"
        opaque
        onClear={handleClear}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

const normalizeInitial = (initial: string): string => {
  if (!initial) return 'その他';
  const upper = initial.toUpperCase();
  if (alphabetList.includes(upper)) {
    return upper;
  }
  return 'その他';
};

export default BrandSelector;
